import apiClientService from "./ApiClientService";

async function parseJsonSafe(res) {
   try {
      return await res.json();
   } catch {
      return null;
   }
}

async function throwFromResponse(res, fallbackMsg) {
   let msg = fallbackMsg;
   try {
      const err = await res.json();
      if (err?.message) {
         msg = Array.isArray(err.message) ? err.message.join(" ") : err.message;
      }
   } catch {}
   throw new Error(msg);
}

function unwrapConversationList(data) {
   if (Array.isArray(data)) return data;
   if (!data || typeof data !== "object") return [];

   const direct = [
      data.data,
      data.conversations,
      data.items,
      data.results,
      data.list,
      data.content,
   ];
   for (const value of direct) {
      if (Array.isArray(value)) return value;
   }

   if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
      return unwrapConversationList(data.data);
   }
   return [];
}

function positiveId(raw) {
   const n = Number(raw);
   return Number.isInteger(n) && n > 0 ? n : undefined;
}

function normalizeChatMessage(raw) {
   if (raw == null || typeof raw !== "object") return null;
   const id = raw.id ?? raw.messageId;
   const body = String(
      raw.body ?? raw.text ?? raw.content ?? raw.message ?? "",
   ).trim();
   if (!id && !body) return null;
   return {
      ...raw,
      id,
      body,
      createdAt: raw.createdAt ?? raw.created_at ?? raw.sentAt ?? null,
      senderRole: raw.senderRole ?? raw.sender_role ?? raw.role ?? "",
      senderId: raw.senderId ?? raw.sender_id ?? raw.userId ?? null,
   };
}

function normalizeChatStudent(raw, studentIdFallback) {
   if (raw && typeof raw === "object") {
      const id =
         positiveId(raw.id) ??
         positiveId(raw.userId) ??
         positiveId(raw.studentId) ??
         positiveId(studentIdFallback);
      if (!id) return null;
      return {
         ...raw,
         id,
         firstName: raw.firstName ?? raw.firstname ?? "",
         lastName: raw.lastName ?? raw.lastname ?? "",
         email: raw.email ?? "",
         phone: raw.phone ?? raw.phoneNumber ?? "",
      };
   }
   const id = positiveId(studentIdFallback);
   return id ? { id } : null;
}

/** Normalize list/detail conversation payloads from GET /chat/conversations. */
export function normalizeStaffConversation(conv) {
   if (conv == null || typeof conv !== "object") return null;

   const id = positiveId(conv.id ?? conv.conversationId);
   if (!id) return null;

   const studentId =
      conv.studentId ??
      conv.student_id ??
      conv.userId ??
      conv.user_id ??
      conv.participantId;

   const student = normalizeChatStudent(
      conv.student ?? conv.user ?? conv.participant ?? conv.studentUser,
      studentId,
   );

   const messages = Array.isArray(conv.messages)
      ? conv.messages.map(normalizeChatMessage).filter(Boolean)
      : [];

   const lastMessage =
      normalizeChatMessage(
         conv.lastMessage ??
            conv.last_message ??
            conv.latestMessage ??
            conv.latest_message,
      ) ?? (messages.length ? messages[messages.length - 1] : null);

   const messageCount = Number(
      conv.messageCount ??
         conv.message_count ??
         conv.messagesCount ??
         messages.length ??
         (lastMessage ? 1 : 0),
   );

   return {
      ...conv,
      id,
      student,
      studentId: student?.id ?? positiveId(studentId) ?? null,
      messages,
      lastMessage,
      messageCount: Number.isFinite(messageCount) ? messageCount : 0,
      updatedAt:
         conv.updatedAt ??
         conv.updated_at ??
         lastMessage?.createdAt ??
         null,
   };
}

/** GET /chat/my-conversation — student */
export async function getMyConversation() {
   const res = await apiClientService.get("/chat/my-conversation");
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut încărca conversația.");
   }
   const data = await parseJsonSafe(res);
   return normalizeStaffConversation(data) ?? data;
}

/** POST /chat/my-conversation/messages — student */
export async function postMyConversationMessage(body) {
   const res = await apiClientService.post(
      "/chat/my-conversation/messages",
      JSON.stringify({ body: String(body || "").trim() }),
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut trimite mesajul.");
   }
   return parseJsonSafe(res);
}

/** GET /chat/conversations — admin/manager */
export async function listStaffConversations() {
   const res = await apiClientService.get("/chat/conversations");
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-au putut încărca conversațiile.");
   }
   const data = await parseJsonSafe(res);
   return unwrapConversationList(data)
      .map(normalizeStaffConversation)
      .filter(Boolean);
}

/** GET /chat/conversations/:id — admin/manager */
export async function getStaffConversation(id) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.get(`/chat/conversations/${safe}`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut încărca conversația.");
   }
   const data = await parseJsonSafe(res);
   return normalizeStaffConversation(data) ?? data;
}

/** POST /chat/conversations — deschide conversație cu un student (admin/manager) */
export async function ensureStaffConversation(studentId) {
   const res = await apiClientService.post(
      "/chat/conversations",
      JSON.stringify({ studentId: Number(studentId) }),
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut deschide conversația.");
   }
   const data = await parseJsonSafe(res);
   return normalizeStaffConversation(data) ?? data;
}

/** POST /chat/conversations/:id/messages — admin/manager */
export async function postStaffConversationMessage(id, body) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.post(
      `/chat/conversations/${safe}/messages`,
      JSON.stringify({ body: String(body || "").trim() }),
      "application/json; charset=UTF-8",
   );
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut trimite mesajul.");
   }
   return parseJsonSafe(res);
}
